import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { redirectTo, useUserService } from '@climate/components'

/**
 * Role that grants access to the Tellus back office. Matched against
 * `active_roles`, which is what the back office itself gates on, so this never
 * sends someone to a door that will be shut in their face.
 */
const ADMIN_ROLE = 'admin:manager'

/** The CRM alone, for sales and marketing (crm/app/auth.py). */
const CRM_ROLE = 'crm:user'

/**
 * Where a signed-in person lands when they arrive at IAM without being sent
 * somewhere specific.
 *
 * `active_applications` is the suite's URL directory and is the same for
 * everybody; `allowed_applications` is what this person may actually open. Only
 * the second one is a permission, so the choice of app is made from it.
 */
export default function Home() {

  const { user } = useUserService(window.config.iam_service_url, window.config.session_cookie, 'iam')
  const navigate = useNavigate()

  useEffect(() => {
    if (user.entity_status !== 'active') {
      navigate('/app/onboard', { replace: true })
      return
    }

    const activeApps = user.active_applications as Record<string, { url: string }> | undefined
    const app = (user.allowed_applications as string[] | undefined)?.[0]
    if (app && activeApps?.[app]) {
      redirectTo(activeApps[app].url)
      return
    }

    // Back-office people hold no member-facing application: approving members
    // is not a member activity. They used to fall through to the 'ppa' default
    // below and land in an empty PPA, which is what a back office operator saw
    // instead of the back office.
    const isAdmin = (user.active_roles as string[] | undefined)?.includes(ADMIN_ROLE)
    if (isAdmin && window.config.admin_app_url) {
      redirectTo(window.config.admin_app_url)
      return
    }

    // Sales and marketing hold the CRM role and nothing else, so they too
    // would land in an empty PPA.
    if ((user.allowed_roles as string[] | undefined)?.includes(CRM_ROLE) && window.config.crm_app_url) {
      redirectTo(window.config.crm_app_url)
      return
    }

    // Nothing else to go on. PPA has always been the fallback and stays one, so
    // that an account in an unexpected state still lands on a real page.
    if (activeApps?.ppa) {
      redirectTo(activeApps.ppa.url)
    }
  }, [user, navigate])

  return <></>
}
